import React, { createContext, useMemo } from 'react';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import MuiTabs, { TabsProps as MuiTabsProps } from '@material-ui/core/Tabs';
import classnames from 'classnames';
import { size } from 'lodash';

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    root: {
      minHeight: unit * 4,
    },
    rootWithGutters: {
      paddingLeft: unit * 3,
      paddingRight: unit * 3,
    },
    indicator: {
      height: 3,
    },
    indicatorHidden: {
      visibility: 'hidden',
    },
    oneTab: {
      maxWidth: 150,
    },
  });

export interface TabsContextProps {
  dense?: boolean;
}

export const TabsContext = createContext<TabsContextProps>({
  dense: false,
});

if (process.env.NODE_ENV !== 'production') {
  TabsContext.displayName = 'TabsContext';
}

export interface TabsProps extends Omit<MuiTabsProps, 'classes'>, WithStyles<typeof styles>, TabsContextProps {
  disableGutters?: boolean;
}

const Tabs: React.FC<TabsProps> = ({ classes, dense, disableGutters, ...rest }) => {
  const context = useMemo(() => ({ dense }), [dense]);

  const oneTab = useMemo(() => size(rest.children as any[]) === 1, [rest.children]);

  return (
    <TabsContext.Provider value={context}>
      <MuiTabs
        classes={{
          root: classnames(classes.root, {
            [classes.rootWithGutters]: !disableGutters,
            [classes.oneTab]: oneTab,
          }),
          indicator: classnames(classes.indicator, {
            [classes.indicatorHidden]: oneTab,
          }),
        }}
        {...rest}
      />
    </TabsContext.Provider>
  );
};

export default withStyles(styles, { name: 'LumaTabs' })(Tabs);
